import { Alert, Button, Form, Input, Modal, Radio, RadioChangeEvent, Result, Select, Space } from 'antd';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ITitleSearch from '~Api/Application/ITitleSearch';
import TitleSearchStatusEnum from '~Api/Application/TitleSearchStatusEnum';
import {
    applicationPropertySelector,
    applicationPropertyTitleSearchAddErrorsSelector,
    applicationPropertyTitleSearchAddressSelector,
    applicationPropertyTitleSearchRequestInProgressSelector,
    applicationPropertyTitleSearchSelector,
} from '~Applications/selectors';
import { IGlobalState } from '~reducer';
import dayjs from 'dayjs';
import {
    applicationPropertyGetAction,
    applicationPropertyTitleSearchAddAction,
    applicationPropertyTitleSearchAddressGetAction,
    applicationPropertyTitleSearchCheckAction,
    applicationPropertyTitleSearchGetAction,
    applicationPropertyTitleSearchOrderAction,
} from '~Applications/actions';
import { Dispatch } from 'redux';
import PropertyStateEnum from '~Api/Deal/PropertyStateEnum';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import _ from 'lodash';
import { FaSyncAlt } from 'react-icons/fa';
import { IDictionary } from '~utilities/IDictionary';
import { DefaultOptionType } from 'antd/lib/select';
import ITitleSearchResult from '~Api/Application/ITitleSearchResult';
import PropertyStreetTypeEnum from '~Api/Deal/PropertyStreetTypeEnum';
import equifaxStreetTypeLabels from '~Api/Deal/equifaxStreetTypeLabels';
import ITitleSearchAddress from '~Api/Application/ITitleSearchAddress';

interface ITitleSearchError {
    postcode?: string;
    state?: string;
    streetName?: string;
    streetType?: string;
    suburb?: string;
    titleSearchResultUuid?: string;
}

interface IDefaultedValues {
    postcode: string;
    state: PropertyStateEnum;
    streetName: string;
    streetNumber: string;
    streetType: PropertyStreetTypeEnum;
    suburb: string;
    unitNumber: string;
}

export default function TitleSearchModal(props: {
    applicationPropertyUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { isOpen, onCancel, applicationPropertyUuid } = props;

    const [ errors, setErrors ] = useState<ITitleSearchError>({});
    const [ dirtyFields, setDirtyFields ] = useState<IDictionary<boolean>>({});

    // Address fields for running title search
    const [ postcode, setPostcode ] = useState<string>(null);
    const [ state, setState ] = useState<PropertyStateEnum>(null);
    const [ streetName, setStreetName ] = useState<string>(null);
    const [ streetNumber, setStreetNumber ] = useState<string>(null);
    const [ streetType, setStreetType ] = useState<PropertyStreetTypeEnum>(null);
    const [ suburb, setSuburb ] = useState<string>(null);
    const [ unitNumber, setUnitNumber ] = useState<string>(null);

    // Fields for ordering title search
    const [ titleSearchResultUuid, setTitleSearchResultUuid ] = useState<string>(null);

    const applicationProperty: IApplicationProperty = useSelector((state: IGlobalState) => applicationPropertySelector(state, applicationPropertyUuid));
    const titleSearch: ITitleSearch = useSelector((state: IGlobalState) => applicationPropertyTitleSearchSelector(state, applicationPropertyUuid));
    const titleSearchAddress: ITitleSearchAddress = useSelector((state: IGlobalState) => applicationPropertyTitleSearchAddressSelector(state, applicationPropertyUuid));
    const titleSearchAddErrors: IDictionary<string> = useSelector((state: IGlobalState) => applicationPropertyTitleSearchAddErrorsSelector(state, applicationPropertyUuid));
    const isRequestInProgress: boolean = useSelector(applicationPropertyTitleSearchRequestInProgressSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!applicationProperty) {
            dispatch(applicationPropertyGetAction(applicationPropertyUuid));
        }
    }, [
        applicationProperty,
        applicationPropertyUuid,
        dispatch,
    ]);

    useEffect(() => {
        if (!titleSearch) {
            dispatch(applicationPropertyTitleSearchGetAction(applicationPropertyUuid));
        }
    }, [
        applicationPropertyUuid,
        dispatch,
        titleSearch,
    ]);

    useEffect(() => {
        if (!titleSearchAddress) {
            dispatch(applicationPropertyTitleSearchAddressGetAction(applicationPropertyUuid));
        }
    }, [
        applicationPropertyUuid,
        dispatch,
        titleSearchAddress,
    ]);

    const defaultedValues: IDefaultedValues = {
        postcode: dirtyFields.postcode ? postcode : (titleSearchAddress ? titleSearchAddress.postcode : null),
        state: dirtyFields.state ? state : (titleSearchAddress ? titleSearchAddress.state : null),
        streetName: dirtyFields.streetName ? streetName : (titleSearchAddress ? titleSearchAddress.streetName : null),
        streetNumber: dirtyFields.streetNumber ? streetNumber : (titleSearchAddress ? titleSearchAddress.streetNumber : null),
        streetType: dirtyFields.streetType ? streetType : (titleSearchAddress ? titleSearchAddress.streetType : null),
        suburb: dirtyFields.suburb ? suburb : (titleSearchAddress ? titleSearchAddress.suburb : null),
        unitNumber: dirtyFields.unitNumber ? unitNumber : (titleSearchAddress ? titleSearchAddress.unitNumber : null),
    };

    const onClickCheckStatus: () => void = useCallback(() => {
        dispatch(applicationPropertyTitleSearchCheckAction(applicationPropertyUuid));
    }, [
        applicationPropertyUuid,
        dispatch,
    ]);

    const validatePostcode: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!defaultedValues.postcode) {
            error = 'Please enter a postcode';
        } else if (defaultedValues.postcode.length !== 4) {
            error = 'Postcode must be exactly 4 digits';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            postcode: error,
        }));

        return !error;
    }, [
        defaultedValues.postcode,
    ]);

    const validateState: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!defaultedValues.state) {
            error = 'Please select a state';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            state: error,
        }));

        return !error;
    }, [
        defaultedValues.state,
    ]);

    const validateStreetName: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!defaultedValues.streetName) {
            error = 'Please enter a street name';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            streetName: error,
        }));

        return !error;
    }, [
        defaultedValues.streetName,
    ]);

    const validateStreetType: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!defaultedValues.streetType || !equifaxStreetTypeLabels[defaultedValues.streetType]) {
            error = 'Please select a street type';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            streetType: error,
        }));

        return !error;
    }, [
        defaultedValues.streetType,
    ]);

    const validateSuburb: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!defaultedValues.suburb) {
            error = 'Please enter a suburb';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            suburb: error,
        }));

        return !error;
    }, [
        defaultedValues.suburb,
    ]);

    const validateTitleSearchResult: () => boolean = useCallback((): boolean => {
        let error: string | null = null;

        if (!titleSearchResultUuid) {
            error = 'Please choose a search result';
        }

        setErrors((prevErrors: ITitleSearchError) => ({
            ...prevErrors,
            titleSearchResultUuid: error,
        }));

        return !error;
    }, [
        titleSearchResultUuid,
    ]);

    const onClickOrderTitleSearch: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateTitleSearchResult() && valid;

        if (!valid) {
            return;
        }

        dispatch(applicationPropertyTitleSearchOrderAction(applicationPropertyUuid, titleSearchResultUuid));
    }, [
        applicationPropertyUuid,
        dispatch,
        titleSearchResultUuid,
        validateTitleSearchResult,
    ]);

    const onClickRunTitleSearch: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateStreetName() && valid;
        valid = validateStreetType() && valid;
        valid = validateSuburb() && valid;
        valid = validateState() && valid;
        valid = validatePostcode() && valid;

        if (!valid) {
            return;
        }

        dispatch(applicationPropertyTitleSearchAddAction(
            applicationPropertyUuid,
            defaultedValues.postcode,
            defaultedValues.state,
            defaultedValues.streetName,
            defaultedValues.streetNumber,
            defaultedValues.streetType,
            defaultedValues.suburb,
            defaultedValues.unitNumber,
        ));
    }, [
        applicationPropertyUuid,
        defaultedValues.postcode,
        defaultedValues.state,
        defaultedValues.streetName,
        defaultedValues.streetNumber,
        defaultedValues.streetType,
        defaultedValues.suburb,
        defaultedValues.unitNumber,
        dispatch,
        validatePostcode,
        validateState,
        validateStreetName,
        validateStreetType,
        validateSuburb,
    ]);

    const onChangePostcode: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostcode(event.target.value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, postcode: true }));
    };

    const onChangeTitleSearchResult: (event: RadioChangeEvent) => void = (event: RadioChangeEvent) => {
        setTitleSearchResultUuid(event.target.value);
    };

    const onChangeState: (value: PropertyStateEnum) => void = (value: PropertyStateEnum) => {
        setState(value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, state: true }));
    };

    const onChangeStreetName: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStreetName(event.target.value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, streetName: true }));
    };

    const onChangeStreetNumber: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStreetNumber(event.target.value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, streetNumber: true }));
    };

    const onChangeStreetType: (value: PropertyStreetTypeEnum) => void = (value: PropertyStreetTypeEnum) => {
        setStreetType(value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, streetType: true }));
    };

    const onChangeSuburb: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSuburb(event.target.value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, suburb: true }));
    };

    const onChangeUnitNumber: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUnitNumber(event.target.value);
        setDirtyFields((prev: IDictionary<boolean>) => ({ ...prev, unitNumber: true }));
    };

    const onStreetTypeFilterOption: (input: string, option: DefaultOptionType) => boolean = (input: string, option: DefaultOptionType) => {
        return option.label.toString().toLocaleLowerCase().includes(input.toLocaleLowerCase());
    };

    if (!applicationProperty || !titleSearch || !titleSearchAddress) {
        return null;
    }

    const titleSearchAddErrorsBlock: JSX.Element = _.keys(titleSearchAddErrors).length > 0 && (
        <Alert
            className='form-error-message'
            description={<ul>{_.map(titleSearchAddErrors, (error: string, index: string) => <li key={index}>{error}</li>)}</ul>}
            message='There was a problem running the title search:'
            showIcon={true}
            type='error'
        />
    );

    let modalBody: JSX.Element = null;
    let modalFooter: JSX.Element = null;

    if (TitleSearchStatusEnum.NotStarted === titleSearch.status) {
        // Step 1: Run Title Search
        modalBody = (
            <>
                {titleSearchAddErrorsBlock}
                <Form.Item label='Property'>
                    {applicationProperty.dealProperty.formattedAddress}
                </Form.Item>
                <Form.Item className='unit-number' label='Unit Number'>
                    <Input max={5} value={defaultedValues.unitNumber} onChange={onChangeUnitNumber} />
                </Form.Item>
                <Form.Item className='street-number' label='Street Number'>
                    <Input max={8} value={defaultedValues.streetNumber} onChange={onChangeStreetNumber} />
                </Form.Item>
                <Form.Item className='street-name' label='Street Name' validateStatus={errors.streetName && 'error'} help={errors.streetName}>
                    <Input max={40} value={defaultedValues.streetName} onChange={onChangeStreetName} onBlur={validateStreetName} />
                </Form.Item>
                <Form.Item className='street-type' label='Street Type' validateStatus={errors.streetType && 'error'} help={errors.streetType}>
                    <Select
                        showSearch={true}
                        optionFilterProp='label'
                        options={_.keys(equifaxStreetTypeLabels).map((streetTypeKey: string): DefaultOptionType => ({ label: equifaxStreetTypeLabels[streetTypeKey], value: streetTypeKey }))}
                        onChange={onChangeStreetType}
                        value={defaultedValues.streetType}
                        filterOption={onStreetTypeFilterOption}
                        onBlur={validateStreetType}
                    />
                </Form.Item>
                <Form.Item className='suburb' label='Suburb' validateStatus={errors.suburb && 'error'} help={errors.suburb}>
                    <Input max={50} value={defaultedValues.suburb} onChange={onChangeSuburb} onBlur={validateSuburb} />
                </Form.Item>
                <Form.Item className='state' label='State' validateStatus={errors.state && 'error'} help={errors.state}>
                    <Select onChange={onChangeState} value={defaultedValues.state} onBlur={validateState}>
                        <Select.Option value={PropertyStateEnum.AustralianCapitalTerritory}>ACT</Select.Option>
                        <Select.Option value={PropertyStateEnum.NewSouthWales}>NSW</Select.Option>
                        <Select.Option value={PropertyStateEnum.NorthernTerritory}>NT</Select.Option>
                        <Select.Option value={PropertyStateEnum.Queensland}>QLD</Select.Option>
                        <Select.Option value={PropertyStateEnum.SouthAustralia}>SA</Select.Option>
                        <Select.Option value={PropertyStateEnum.Tasmania}>TAS</Select.Option>
                        <Select.Option value={PropertyStateEnum.Victoria}>VIC</Select.Option>
                        <Select.Option value={PropertyStateEnum.WesternAustralia}>WA</Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item className='postcode' label='Postcode' validateStatus={errors.postcode && 'error'} help={errors.postcode}>
                    <Input value={defaultedValues.postcode} onChange={onChangePostcode} onBlur={validatePostcode} />
                </Form.Item>
            </>
        );

        modalFooter = (
            <Button
                loading={isRequestInProgress}
                type='primary'
                onClick={onClickRunTitleSearch}
            >Run Title Search</Button>
        );
    } else if (TitleSearchStatusEnum.Searched === titleSearch.status && titleSearch.results.length < 1) {
        // Step 2: Order Title Search - no results
        modalBody = (
            <Result
                status='warning'
                subTitle='No matching titles found for this address.'
            />
        );

        modalFooter = (
            <Button onClick={onCancel}>Close</Button>
        );
    } else if (TitleSearchStatusEnum.Searched === titleSearch.status) {
        // Step 2: Order Title Search
        const titleSearchResultsBlock: JSX.Element[] = titleSearch.results.map((result: ITitleSearchResult) => (
            <Radio key={result.uuid} value={result.uuid}>
                {result.titleDescription}
                <span className='title-reference'>{result.titleReference}</span>
            </Radio>
        ));

        modalBody = (
            <Form.Item className='title-search-result' label='Address' validateStatus={errors.titleSearchResultUuid && 'error'} help={errors.titleSearchResultUuid}>
                <Radio.Group onChange={onChangeTitleSearchResult} value={titleSearchResultUuid} onBlur={validateTitleSearchResult}>
                    <Space direction='vertical'>
                        {titleSearchResultsBlock}
                    </Space>
                </Radio.Group>
            </Form.Item>
        );

        modalFooter = (
            <Button
                loading={isRequestInProgress}
                type='primary'
                onClick={onClickOrderTitleSearch}
            >Order Title Search</Button>
        );
    } else if (TitleSearchStatusEnum.Ordered === titleSearch.status) {
        // Step 3: Check Title Status
        const checkTitleStatusButton: JSX.Element = (
            <Button
                key='check-title-status'
                loading={isRequestInProgress}
                onClick={onClickCheckStatus}
            >Check Title Status</Button>
        );

        modalBody = (
            <Result
                icon={<FaSyncAlt className='check-title-status-icon' size={72} />}
                subTitle={`A title search is currently in progress as of ${dayjs(titleSearch.createdTime).format('DD/MM/YYYY HH:mm')}`}
                extra={[ checkTitleStatusButton ]}
            />
        );
    } else if (TitleSearchStatusEnum.Completed === titleSearch.status) {
        // Step 4: Title Search Completed
        modalBody = (
            <Result
                status='success'
                subTitle='The title search has completed and is available in Underwriting.'
            />
        );

        modalFooter = (
            <Button onClick={onCancel}>Close</Button>
        );
    }

    return (
        <Modal
            footer={modalFooter}
            onCancel={onCancel}
            open={isOpen}
            title='Title Search'
            wrapClassName='application-title-search-modal'
        >
            {modalBody}
        </Modal>
    );
}
